import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/api';
import { ADMIN_OPS_REFRESH } from '../constants/adminEvents';
import {
  buildDropshipWhatsAppMessage,
  fulfillmentLabel,
  fulfillmentSummaryLabel,
  isDropshipping
} from '../utils/fulfillment';
import {
  SHIPPING_CARRIERS,
  carrierLabel,
  carrierTrackingRequired
} from '../constants/shippingCarriers';
import './OrdersManagement.css';

const QUEUE_OPTIONS = [
  { value: '', label: 'Todas las órdenes' },
  { value: 'dropshipping', label: 'Cola dropshipping' },
  { value: 'packaging', label: 'Cola empaque Chía' },
  { value: 'shipping_guide', label: 'Listos para guía' },
  { value: 'payment_pending', label: 'Pagos abiertos' },
  { value: 'attention', label: 'Requieren atención' }
];

const QUEUE_TITLES = {
  dropshipping: 'Cola dropshipping',
  packaging: 'Cola empaque Chía',
  shipping_guide: 'Guías y envío',
  payment_pending: 'Pagos abiertos',
  attention: 'Órdenes que requieren atención',
  '': 'Todas las órdenes'
};

const OrdersManagement = ({ initialQueue = '' }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filterPayment, setFilterPayment] = useState('');
  const [filterQueue, setFilterQueue] = useState(initialQueue || '');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [actionOrderId, setActionOrderId] = useState(null);
  const [trackingDraft, setTrackingDraft] = useState({});
  const [carrierDraft, setCarrierDraft] = useState({});
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [copyFeedbackId, setCopyFeedbackId] = useState(null);

  useEffect(() => {
    setFilterQueue(initialQueue || '');
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [initialQueue]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: pagination.page, limit: pagination.limit, include_items: '1' };
      if (filterPayment) params.payment_status = filterPayment;
      if (filterQueue) params.queue = filterQueue;
      const res = await adminService.getOrders(params);
      const list = res?.orders ?? res?.data?.orders ?? [];
      setOrders(list);
      setPagination((prev) => ({
        ...prev,
        ...(res?.pagination ?? res?.data?.pagination ?? {}),
        total: res?.pagination?.total ?? res?.data?.pagination?.total ?? list.length,
        pages: res?.pagination?.pages ?? res?.data?.pagination?.pages ?? 1
      }));
    } catch (error) {
      console.error('Error fetching orders:', error);
      setMessage({ type: 'error', text: 'Error al cargar órdenes' });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterPayment, filterQueue]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value || 0);

  const getPaymentStatusBadge = (status) => {
    const badges = {
      paid: { label: 'Pagado', class: 'status-paid' },
      pending: { label: 'Pendiente', class: 'status-pending' },
      failed: { label: 'Fallido', class: 'status-failed' }
    };
    const b = badges[status] || { label: status || '-', class: 'status-default' };
    return <span className={`status-badge ${b.class}`}>{b.label}</span>;
  };

  const getOrderStatusBadge = (status) => {
    const badges = {
      pending: { label: 'Pendiente', class: 'status-pending' },
      awaiting_packaging: { label: 'En empaque', class: 'status-awaiting_packaging' },
      ready_to_ship: { label: 'Listo legacy', class: 'status-ready_to_ship' },
      processing: { label: 'Procesando', class: 'status-processing' },
      shipped: { label: 'Enviado', class: 'status-shipped' },
      delivered: { label: 'Entregado', class: 'status-delivered' },
      cancelled: { label: 'Cancelado', class: 'status-cancelled' }
    };
    const b = badges[status] || { label: status || '-', class: 'status-default' };
    return <span className={`status-badge ${b.class}`}>{b.label}</span>;
  };

  const getFulfillmentBadge = (summary) => {
    const cls =
      summary === 'dropshipping'
        ? 'fulfillment-badge fulfillment-badge--dropship'
        : summary === 'mixed'
          ? 'fulfillment-badge fulfillment-badge--mixed'
          : 'fulfillment-badge fulfillment-badge--javiland';
    return <span className={cls}>{fulfillmentSummaryLabel(summary)}</span>;
  };

  const setTracking = (orderId, value) => {
    setTrackingDraft((prev) => ({ ...prev, [orderId]: value }));
  };

  const callUpdate = async (orderId, body) => {
    try {
      setActionOrderId(orderId);
      setMessage({ type: '', text: '' });
      await adminService.updateOrderStatus(orderId, body);
      setMessage({ type: 'success', text: 'Orden actualizada' });
      await fetchOrders();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Error al actualizar'
      });
    } finally {
      setActionOrderId(null);
    }
  };

  const callMarkAsPaid = async (orderId) => {
    if (
      !window.confirm(
        '¿Confirmar que Wompi cobró y registrar pago? Se creará la factura pendiente y se enviará el correo de pago aprobado.'
      )
    ) {
      return;
    }
    try {
      setActionOrderId(orderId);
      setMessage({ type: '', text: '' });
      await adminService.markOrderAsPaid(orderId);
      setMessage({ type: 'success', text: 'Pago registrado; pedido en cola operativa' });
      await fetchOrders();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Error al marcar pago'
      });
    } finally {
      setActionOrderId(null);
    }
  };

  const copyDropshipMessage = async (order) => {
    const text = buildDropshipWhatsAppMessage(order);
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedbackId(order.id);
      window.setTimeout(() => setCopyFeedbackId(null), 2000);
    } catch {
      window.prompt('Copia el mensaje para WhatsApp:', text);
    }
  };

  const markDropshipHandled = async (order) => {
    if (
      !window.confirm(
        '¿Marcar dropshipping como gestionado con proveedor? La orden pasará a procesando (no requiere empaque en Chía).'
      )
    ) {
      return;
    }
    await callUpdate(order.id, { status: 'processing' });
  };

  const toggleExpand = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const formatAddress = (order) => {
    const parts = [
      order.shipping_address,
      order.shipping_city,
      order.shipping_department,
      order.shipping_postal_code
    ].filter((p) => p != null && String(p).trim() !== '');
    return parts.length ? parts.join(', ') : '—';
  };

  const setCarrier = (orderId, value) => {
    setCarrierDraft((prev) => ({ ...prev, [orderId]: value }));
  };

  const getCarrierForOrder = (order) =>
    carrierDraft[order.id] ?? order.shipping_carrier ?? 'mee';

  const markPackagingDone = async (order) => {
    if (
      !window.confirm(
        '¿Marcar empaquetado y pasar la orden a guías y envío? (Para piloto CHK-001 usa la vista Empaque Chía.)'
      )
    ) {
      return;
    }
    await callUpdate(order.id, { status: 'processing' });
  };

  const renderOrderActions = (order) => {
    const hasDropship =
      order.fulfillment_summary === 'dropshipping' ||
      order.fulfillment_summary === 'mixed' ||
      (order.items || []).some((it) => isDropshipping(it.fulfillment));
    const busy = actionOrderId === order.id;

    return (
      <div className="order-actions-bar">
        {hasDropship &&
          order.payment_status === 'paid' &&
          ['awaiting_packaging', 'ready_to_ship', 'processing'].includes(order.status) && (
            <>
              <button
                type="button"
                className="btn-ord btn-ord-whatsapp"
                onClick={() => copyDropshipMessage(order)}
              >
                {copyFeedbackId === order.id ? 'Copiado' : 'WhatsApp proveedor'}
              </button>
              {['awaiting_packaging', 'ready_to_ship'].includes(order.status) &&
                order.fulfillment_summary === 'dropshipping' && (
                  <button
                    type="button"
                    className="btn-ord"
                    disabled={busy}
                    onClick={() => markDropshipHandled(order)}
                  >
                    Dropship gestionado
                  </button>
                )}
            </>
          )}
        {order.payment_status === 'pending' && order.status === 'pending' && (
          <button
            type="button"
            className="btn-ord btn-ord-paid"
            disabled={busy}
            onClick={() => callMarkAsPaid(order.id)}
          >
            Marcar pagado
          </button>
        )}
        {order.payment_status === 'paid' &&
          ['awaiting_packaging', 'ready_to_ship'].includes(order.status) &&
          order.fulfillment_summary === 'javiland' && (
            <button
              type="button"
              className="btn-ord btn-ord-primary"
              disabled={busy}
              onClick={() => markPackagingDone(order)}
            >
              Empaque listo → Guías
            </button>
          )}
        {order.status === 'processing' && (
          <div className="ship-dispatch-inline">
            <label className="ship-carrier-label">
              Proveedor
              <select
                className="ship-carrier-select"
                value={getCarrierForOrder(order)}
                onChange={(e) => setCarrier(order.id, e.target.value)}
              >
                {SHIPPING_CARRIERS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <input
              type="text"
              className="ship-tracking-input"
              placeholder={
                SHIPPING_CARRIERS.find((c) => c.value === getCarrierForOrder(order))
                  ?.trackingPlaceholder || 'N° guía'
              }
              value={trackingDraft[order.id] ?? ''}
              onChange={(e) => setTracking(order.id, e.target.value)}
            />
            <button
              type="button"
              className="btn-ord btn-ord-primary"
              disabled={busy}
              onClick={() => {
                const carrier = getCarrierForOrder(order);
                const tr = (trackingDraft[order.id] || '').trim();
                if (carrierTrackingRequired(carrier) && !tr) {
                  setMessage({
                    type: 'error',
                    text: 'Indica el número de guía para este proveedor'
                  });
                  return;
                }
                callUpdate(order.id, {
                  status: 'shipped',
                  tracking_number: tr,
                  shipping_carrier: carrier
                });
              }}
            >
              Despachar
            </button>
          </div>
        )}
        {order.status === 'shipped' && (
          <button
            type="button"
            className="btn-ord btn-ord-primary"
            disabled={busy}
            onClick={() => callUpdate(order.id, { status: 'delivered' })}
          >
            Marcar entregado
          </button>
        )}
        {['pending', 'processing', 'awaiting_packaging', 'ready_to_ship'].includes(order.status) && (
          <button
            type="button"
            className="btn-ord btn-ord-danger"
            disabled={busy}
            onClick={() => {
              if (window.confirm('¿Cancelar esta orden?')) {
                callUpdate(order.id, { status: 'cancelled' });
              }
            }}
          >
            Cancelar
          </button>
        )}
        {busy && <span className="order-actions-busy">Actualizando…</span>}
      </div>
    );
  };

  const title = QUEUE_TITLES[filterQueue] || 'Órdenes';
  const isDropshipQueue = filterQueue === 'dropshipping';

  return (
    <div className="orders-management">
      <div className="container">
        <div className="section-header">
          <h2>{title}</h2>
          <p>
            {isDropshipQueue
              ? 'Pedidos pagados con líneas dropshipping. Copia el mensaje y envíalo al proveedor.'
              : filterQueue === 'shipping_guide'
                ? 'Pedidos empaquetados listos para despacho. Elige proveedor (Javiland local / Mis Envíos Express / Envia legacy).'
                : 'Gestión de pedidos. Bodega Chía: confirma empaque antes de guías (cola Empaque Chía).'}
          </p>
        </div>

        {message.text && (
          <div className={`message-banner ${message.type}`}>
            {message.text}
            <button type="button" onClick={() => setMessage({ type: '', text: '' })}>
              ×
            </button>
          </div>
        )}

        <div className="filters filters--row">
          <select
            value={filterQueue}
            onChange={(e) => {
              setFilterQueue(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="filter-select"
            aria-label="Cola operativa"
          >
            {QUEUE_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={filterPayment}
            onChange={(e) => {
              setFilterPayment(e.target.value);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
            className="filter-select"
            aria-label="Estado de pago"
          >
            <option value="">Todos los pagos</option>
            <option value="paid">Pagados</option>
            <option value="pending">Pendientes</option>
            <option value="failed">Fallidos</option>
          </select>
        </div>

        {loading ? (
          <div className="loading-container">
            <i className="fas fa-spinner fa-spin" />
            <span>Cargando órdenes...</span>
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-shopping-cart" />
            <h3>No hay órdenes en esta cola</h3>
            <p>Prueba otro filtro o espera nuevas ventas en checkout.</p>
          </div>
        ) : (
          <>
            <div className="orders-table-wrapper">
              <table className="orders-table">
                <thead>
                  <tr>
                    <th aria-label="Expandir" />
                    <th>Orden</th>
                    <th>Cliente</th>
                    <th>Despacho</th>
                    <th>Total</th>
                    <th>Fecha</th>
                    <th>Pago</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => {
                    const expanded = expandedOrderId === order.id;
                    const hasDropship =
                      order.fulfillment_summary === 'dropshipping' ||
                      order.fulfillment_summary === 'mixed' ||
                      (order.items || []).some((it) => isDropshipping(it.fulfillment));
                    return (
                      <React.Fragment key={order.id}>
                        <tr className={expanded ? 'order-row-expanded' : ''}>
                          <td>
                            <button
                              type="button"
                              className="btn-expand-order"
                              onClick={() => toggleExpand(order.id)}
                              aria-expanded={expanded}
                              aria-label={expanded ? 'Ocultar detalle' : 'Ver detalle'}
                            >
                              <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} />
                            </button>
                          </td>
                          <td className="order-cell">
                            <strong>{order.order_number || `#${order.id}`}</strong>
                            {order.tracking_number ? (
                              <div className="order-tracking-hint">Guía: {order.tracking_number}</div>
                            ) : null}
                            {order.shipping_carrier ? (
                              <div className="order-tracking-hint">{carrierLabel(order.shipping_carrier)}</div>
                            ) : null}
                          </td>
                          <td>
                            <div className="customer-cell">
                              <strong>{order.customer_name || '-'}</strong>
                              <small>{order.customer_email}</small>
                              {order.customer_phone ? <small>{order.customer_phone}</small> : null}
                            </div>
                          </td>
                          <td>{getFulfillmentBadge(order.fulfillment_summary || 'javiland')}</td>
                          <td>{formatCurrency(order.total)}</td>
                          <td>{formatDate(order.created_at)}</td>
                          <td>{getPaymentStatusBadge(order.payment_status)}</td>
                          <td>{getOrderStatusBadge(order.status)}</td>
                        </tr>
                        <tr className="order-actions-row">
                          <td colSpan={8}>{renderOrderActions(order)}</td>
                        </tr>
                        {expanded && (
                          <tr className="order-detail-row">
                            <td colSpan={8}>
                              <div className="order-detail-panel">
                                <div className="order-detail-grid">
                                  <div>
                                    <h4>Envío</h4>
                                    <p>{formatAddress(order)}</p>
                                  </div>
                                  <div>
                                    <h4>Ítems</h4>
                                    <ul className="order-detail-items">
                                      {(order.items || []).map((item) => (
                                        <li key={item.id ?? `${item.product_id}-${item.product_name}`}>
                                          <span>
                                            {item.quantity}x {item.product_name}
                                          </span>
                                          <span className="order-detail-item-meta">
                                            {fulfillmentLabel(item.fulfillment)}
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                                {hasDropship && (
                                  <button
                                    type="button"
                                    className="btn-ord btn-ord-whatsapp"
                                    onClick={() => copyDropshipMessage(order)}
                                  >
                                    Copiar mensaje WhatsApp (proveedor)
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  type="button"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  ← Anterior
                </button>
                <span>
                  Página {pagination.page} de {pagination.pages}
                </span>
                <button
                  type="button"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  Siguiente →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OrdersManagement;
